/**
 * GitHubIntegration Component
 *
 * Settings card for configuring GitHub integration:
 * - GitHub Organization name
 * - Repository prefix (mandatory prefix for all repos)
 * - GitHub Personal Access Token (stored as encrypted secret)
 *
 * The organization and prefix are saved to tenant.settings.
 * The GitHub PAT is saved as a secret with code "github-token".
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { Badge } from '../ui/Badge';
import { tenantService } from '../../api/tenant.service';
import { secretService } from '../../api/secret.service';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../lib/utils/errors';
import type { Tenant } from '../../types/tenant.types';

interface GitHubIntegrationProps {
  tenant: Tenant;
  canEdit: boolean;
}

export const GitHubIntegration: React.FC<GitHubIntegrationProps> = ({
  tenant,
  canEdit,
}) => {
  const { updateTenant } = useAuth();

  // Form state
  const [githubOrganization, setGithubOrganization] = useState(
    (tenant.settings?.githubOrganization as string) || ''
  );
  const [repositoryPrefix, setRepositoryPrefix] = useState(
    (tenant.settings?.repositoryPrefix as string) || ''
  );
  const [defaultBranch, setDefaultBranch] = useState(
    (tenant.settings?.defaultBranch as string) || ''
  );
  const [githubToken, setGithubToken] = useState('');
  const [hasExistingToken, setHasExistingToken] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingToken, setIsSavingToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenSuccess, setTokenSuccess] = useState<string | null>(null);

  // Check for existing github-token secret
  useEffect(() => {
    const checkExistingToken = async () => {
      try {
        const secrets = await secretService.listSecrets(tenant.tenantId);
        const hasToken = secrets.some((s) => s.secretCode === 'github-token');
        setHasExistingToken(hasToken);
      } catch {
        // Ignore - user may not have permission to list secrets
      }
    };
    checkExistingToken();
  }, [tenant.tenantId]);

  const handleSaveSettings = async () => {
    setError(null);
    setSuccess(null);
    setIsSaving(true);

    try {
      const updatedTenant = await tenantService.updateTenant(tenant.tenantId, {
        settings: {
          ...tenant.settings,
          githubOrganization: githubOrganization.trim(),
          repositoryPrefix: repositoryPrefix.trim(),
          defaultBranch: defaultBranch.trim(),
        },
      });
      updateTenant(updatedTenant);
      setSuccess('GitHub settings saved successfully.');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveToken = async () => {
    if (!githubToken.trim()) return;

    setTokenError(null);
    setTokenSuccess(null);
    setIsSavingToken(true);

    try {
      if (hasExistingToken) {
        // Find existing secret and update it
        const secrets = await secretService.listSecrets(tenant.tenantId);
        const existing = secrets.find((s) => s.secretCode === 'github-token');
        if (existing) {
          await secretService.updateSecret(tenant.tenantId, existing.secretId, {
            value: githubToken.trim(),
          });
        }
      } else {
        // Create new secret
        await secretService.createSecret(tenant.tenantId, {
          secretName: 'GitHub Token',
          secretCode: 'github-token',
          value: githubToken.trim(),
          description: 'GitHub Personal Access Token for AI Application Setup',
        });
      }

      setHasExistingToken(true);
      setGithubToken('');
      setTokenSuccess(hasExistingToken ? 'GitHub token updated.' : 'GitHub token saved.');
    } catch (err) {
      setTokenError(getErrorMessage(err));
    } finally {
      setIsSavingToken(false);
    }
  };

  const isConfigured = !!(tenant.settings?.githubOrganization && tenant.settings?.repositoryPrefix && tenant.settings?.defaultBranch);

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Card.Title>GitHub Integration</Card.Title>
          </div>
          {isConfigured && hasExistingToken ? (
            <Badge variant="success">Configured</Badge>
          ) : (
            <Badge variant="warning">Not Configured</Badge>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-6">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Configure GitHub integration to enable the AI Application Setup feature.
            This allows you to create applications with automatic repository creation and CI/CD configuration.
          </p>

          {/* Organization and Prefix */}
          <div className="space-y-4">
            {error && <Alert variant="error">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Input
                  label="GitHub Organization"
                  placeholder="my-org"
                  value={githubOrganization}
                  onChange={(e) => setGithubOrganization(e.target.value)}
                  disabled={!canEdit || isSaving}
                />
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  The GitHub organization where repositories will be created.
                </p>
              </div>

              <div>
                <Input
                  label="Repository Prefix"
                  placeholder="ketrics-app-"
                  value={repositoryPrefix}
                  onChange={(e) => setRepositoryPrefix(e.target.value)}
                  disabled={!canEdit || isSaving}
                />
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Mandatory prefix for all repository names (e.g., "ketrics-app-").
                </p>
              </div>

              <div>
                <Input
                  label="Default Branch"
                  placeholder="main"
                  value={defaultBranch}
                  onChange={(e) => setDefaultBranch(e.target.value)}
                  disabled={!canEdit || isSaving}
                />
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  The default branch for new repositories (e.g., "main" or "master").
                </p>
              </div>
            </div>

            {canEdit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSaveSettings}
                loading={isSaving}
                disabled={isSaving || !githubOrganization.trim() || !repositoryPrefix.trim() || !defaultBranch.trim()}
              >
                Save Settings
              </Button>
            )}
          </div>

          {/* GitHub Token */}
          <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="space-y-4">
              {tokenError && <Alert variant="error">{tokenError}</Alert>}
              {tokenSuccess && <Alert variant="success">{tokenSuccess}</Alert>}

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                    GitHub Personal Access Token
                  </label>
                  {hasExistingToken && (
                    <Badge variant="success">Saved</Badge>
                  )}
                </div>
                <Input
                  type="password"
                  placeholder={hasExistingToken ? 'Enter new token to update...' : 'ghp_xxxxxxxxxxxx'}
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  disabled={!canEdit || isSavingToken}
                />
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  A GitHub PAT with <code>repo</code> scope. Stored encrypted in your secrets vault as "github-token".
                  {hasExistingToken && ' Leave blank to keep the current token.'}
                </p>
              </div>

              {canEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSaveToken}
                  loading={isSavingToken}
                  disabled={isSavingToken || !githubToken.trim()}
                >
                  {hasExistingToken ? 'Update Token' : 'Save Token'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
